import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AdventureBadgeInfo } from "./adventure_floor_info.js";

export class AdventureProgressBar
{
  private readonly ctx: CanvasRenderingContext2D;

  private readonly BG_W = 80;
  private readonly BG_CAP_H = 78;

  private readonly CIRCLE_POP_MS = 200;

  private readonly ANIM_BASE_MS = 300;
  private readonly ANIM_PER_LEVEL_MS = 70;
  private readonly ANIM_MAX_MS = 1800;

  private progress_bg?: UINineSliceElement;

  constructor( ctx: CanvasRenderingContext2D )
  {
    this.ctx = ctx;
  }

  build( content: UIElement, badge_infos: AdventureBadgeInfo[], current_level: number, from_level: number ): void
  {
    if ( badge_infos.length === 0 )
      return;

    if ( current_level <= 0 )
    {
      this.buildZeroMarker( content, badge_infos[0] );
      return;
    }

    this.buildFill( content, badge_infos, current_level, from_level );
    this.buildCircles( content, badge_infos, current_level, from_level );
  }

  computeMarkerY( badge_infos: AdventureBadgeInfo[], level: number ): number | null
  {
    if ( badge_infos.length === 0 )
      return null;

    return this.markerYForLevel( badge_infos, level );
  }

  private buildFill( content: UIElement, badge_infos: AdventureBadgeInfo[], current_level: number, from_level: number ): void
  {
    if ( badge_infos.length === 0 )
      return;

    const width = LayoutScaler.widthFromDesign( this.BG_W );
    const cap_h = LayoutScaler.heightFromDesign( this.BG_CAP_H );

    const center_x = this.badgeCenterX( badge_infos[0].x );
    const bottom_center_y = this.badgeCenterY( badge_infos[0].y );
    const marker_y = this.markerYForLevel( badge_infos, current_level );

    const duration = this.animDuration( current_level, from_level );
    const from_marker_y = duration > 0 ? this.markerYForLevel( badge_infos, from_level ) : marker_y;

    if ( marker_y < bottom_center_y )
    {
      if ( duration > 0 )
        this.animateSegment( content, center_x, bottom_center_y, from_marker_y, marker_y, width, cap_h, duration );
      else
        this.drawSegment( content, center_x, bottom_center_y, marker_y, width, cap_h );
    }

    // The lower cap is the permanent beginning of the tower.
    this.buildCurrentLevelMarker( content, center_x, bottom_center_y, bottom_center_y, 0, 0, 0 );

    // At an exact floor threshold the floor badge owns the label. This avoids
    // drawing the progress counter and the floor requirement on top of each other.
    const is_exact_floor = badge_infos.some( info => info.floor_info.level === current_level );
    if ( !is_exact_floor )
      this.buildCurrentLevelMarker( content, center_x, from_marker_y, marker_y, current_level, from_level, duration );
  }

  private buildCircles( content: UIElement, badge_infos: AdventureBadgeInfo[], current_level: number, from_level: number ): void
  {
    const size = LayoutScaler.widthFromDesign( this.BG_W );
    const duration = this.animDuration( current_level, from_level );
    const delta = current_level - from_level;
    const anim_now = performance.now();

    const circleDelay = ( level: number ): number | null =>
    {
      if ( duration <= 0 || delta <= 0 || level <= from_level )
        return null;
      return duration * ( level - from_level ) / delta;
    };

    for ( let i = 1; i < badge_infos.length; i++ )
    {
      const info = badge_infos[i];

      // A floor receives its separate circle only after the player has moved
      // above it. At the exact threshold the progress_bg top cap owns it.
      if ( current_level > info.floor_info.level )
        this.addCircle( content, info, size, ImgID.UI_AdventureProgressCircle, circleDelay( info.floor_info.level ), anim_now );
    }
  }

  private buildZeroMarker( content: UIElement, info: AdventureBadgeInfo ): void
  {
    const center_x = this.badgeCenterX( info.x );
    const center_y = this.badgeCenterY( info.y );
    const size = LayoutScaler.widthFromDesign( this.BG_W );

    content.addChild( new UIImage(
      this.ctx,
      center_x - size / 2,
      center_y - size / 2,
      size,
      size,
      ImgID.UI_AdventureProgressCircleFirst
    ) );

    this.buildCurrentLevelMarker( content, center_x, center_y, center_y, 0, 0, 0 );
  }

  private addCircle( content: UIElement, info: AdventureBadgeInfo, size: number, img_id: ImgID, delay_ms: number | null, anim_now: number ): void
  {
    const center_x = this.badgeCenterX( info.x );
    const center_y = this.badgeCenterY( info.y );

    const circle = new UIImage( this.ctx, center_x - size / 2, center_y - size / 2, size, size, img_id );
    content.addChild( circle );

    if ( delay_ms !== null )
    {
      circle.setOpacityImmediate( 0 );
      circle.fadeIn( this.CIRCLE_POP_MS, anim_now + delay_ms, Easing.EaseOutQuad );
    }
  }

  private buildCurrentLevelMarker( content: UIElement, center_x: number, from_center_y: number, to_center_y: number, current_level: number, from_level: number, duration_ms: number ): void
  {
    const max_digit_count = Math.max( current_level, from_level ).toString().length;
    const num_w = LayoutScaler.widthFromDesign(
      max_digit_count >= 3 ? 68 : max_digit_count === 2 ? 58 : 50
    );
    const num_h = LayoutScaler.heightFromDesign( 55 );
    const num_x = center_x - num_w / 2;

    const should_animate = duration_ms > 0 && from_level < current_level;
    const start_center_y = should_animate ? from_center_y : to_center_y;
    const num_y = start_center_y - num_h / 2;
    const initial_text = ( should_animate ? from_level : current_level ).toString();

    const base_style = {
      text: initial_text,
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_weight: 900,
      font_size: LayoutScaler.textSizeFromDesign(
        max_digit_count >= 3 ? 30 : max_digit_count === 2 ? 36 : 41
      ),
      min_font_size: LayoutScaler.textSizeFromDesign( 22 ),
      auto_wrap: false,
      fit_font_size: true,
      text_color: ColorScheme.white,
    };

    const num = new UITextElement( this.ctx, num_x, num_y, num_w, num_h, {
      ...base_style,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
      outline_color: ColorScheme.adventure_level_marker_outline,
    } );
    const num_1 = new UITextElement( this.ctx, num_x, num_y, num_w, num_h, base_style );

    content.addChild( num );
    content.addChild( num_1 );

    if ( !should_animate )
      return;

    const now = performance.now();
    const counter_options = { from_value: from_level, to_value: current_level, duration_ms, easing: Easing.EaseOutQuad };
    num.animateCounter( now, counter_options );
    num_1.animateCounter( now, counter_options );

    const move_target = new Vector2( num_x, to_center_y - num_h / 2 );
    num.addTransform( new TransformStraightMove( num, true, null, move_target, now, duration_ms, { easing: Easing.EaseOutQuad } ) );
    num_1.addTransform( new TransformStraightMove( num_1, true, null, move_target, now, duration_ms, { easing: Easing.EaseOutQuad } ) );
  }

  private drawSegment( content: UIElement, center_x: number, bottom_center_y: number, top_center_y: number, width: number, cap_h: number ): void
  {
    const seg_y = top_center_y - cap_h / 2;
    const seg_h = ( bottom_center_y - top_center_y ) + cap_h;
    const seg_x = center_x - width / 2;

    const progress_bg = new UINineSliceElement( this.ctx, seg_x, seg_y, width, seg_h, ImgID.UI_AdventureProgressBg, "adventure_progress_bg" );
    this.progress_bg = progress_bg;
    content.addChild( progress_bg );
  }

  private animateSegment( content: UIElement, center_x: number, bottom_center_y: number, from_top_center_y: number, to_top_center_y: number, width: number, cap_h: number, duration_ms: number ): void
  {
    this.drawSegment( content, center_x, bottom_center_y, from_top_center_y, width, cap_h );
    const segment = this.progress_bg!;
    const seg_x = center_x - width / 2;

    const apply = ( top_center_y: number ): void =>
    {
      segment.setPixelPos( new Vector2( seg_x, top_center_y - cap_h / 2 ), true );
      segment.setHeight( ( bottom_center_y - top_center_y ) + cap_h );
    };

    const grow = new TransformValue( from_top_center_y, to_top_center_y, performance.now(), duration_ms, apply, { easing: Easing.EaseOutQuad } );
    grow.setDomSample( top_center_y => ( {
      pos: new Vector2( seg_x, top_center_y - cap_h / 2 ),
      height: ( bottom_center_y - top_center_y ) + cap_h,
    } ) );
    segment.addTransform( grow );
  }

  private markerYForLevel( badge_infos: AdventureBadgeInfo[], level: number ): number
  {
    const zero_center_y = this.badgeCenterY( badge_infos[0].y );
    if ( level <= 0 )
      return zero_center_y;

    for ( let i = 1; i < badge_infos.length; i++ )
    {
      const upper = badge_infos[i];
      if ( level > upper.floor_info.level )
        continue;

      const lower = badge_infos[i - 1];
      const lower_y = this.badgeCenterY( lower.y );
      const upper_y = this.badgeCenterY( upper.y );
      const span = upper.floor_info.level - lower.floor_info.level;
      const t = span > 0 ? ( level - lower.floor_info.level ) / span : 1;
      const clamped_t = Math.max( 0, Math.min( 1, t ) );
      return lower_y + ( upper_y - lower_y ) * clamped_t;
    }

    return this.badgeCenterY( badge_infos[badge_infos.length - 1].y );
  }

  private animDuration( current_level: number, from_level: number ): number
  {
    const delta = current_level - from_level;
    if ( delta <= 0 )
      return 0;

    return Math.min( this.ANIM_MAX_MS, this.ANIM_BASE_MS + delta * this.ANIM_PER_LEVEL_MS );
  }

  private badgeCenterX( badge_x: number ): number
  {
    return badge_x + LayoutScaler.widthFromDesign( 25 );
  }

  private badgeCenterY( badge_y: number ): number
  {
    return badge_y + LayoutScaler.heightFromDesign( 27.5 );
  }
}
